import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { setConfiguration, setPortalBootReady } from '~/store/modules/configuration/configuration.actions';

import api from '~/services/api';

import {
  Layout,
  Drawer,
  Sider,
  Logo,
  Menu,
  Header,
  Collapse,
  Title,
  Acessibility,
  Content,
  Footer,
  BackTopPage,
  Icons,
  Socials,
} from '~/components';
import * as SiderStyled from '~/components/Sider/Sider.styled';
import {
  HeaderTitleSlot,
  HeaderAcessibilitySlot,
} from '~/components/Header/Header.styled';
import { getFullUrl } from '~/services/FileManager/file-manager.service';
import { useMenus } from '~/contexts/menu.context';
import {
  ensureContrast,
  ensureBackgroundForForeground,
  AA_LINK_ON_LIGHT,
  AA_LINK_HOVER_ON_LIGHT,
  AA_TEXT_ON_LIGHT,
  AA_TEXT_PRIMARY_ON_LIGHT,
  AA_SURFACE,
} from '~/modules/Helper/contrast.helper';

function DefaultLayout({ children }) {
  const { isLoadingMenus } = useMenus();
  const [isLoading, setIsLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [backTopBgColor, setBackTopBgColor] = useState();
  const [showLoadingText, setShowLoadingText] = useState(false);

  const dispatch = useDispatch();

  const isMobile = useMediaQuery({ query: '(max-width: 700px)' });

  useEffect(() => {
    let isMounted = true;

    async function getConfiguracao() {
      try {
        const [responseConfiguracao, responseCliente] = await Promise.all([
          api.get('configuracaoaparencia'),
          api.get('cliente'),
        ]);

        const {
          BannerUrl,
          BrasaoUrl,
          BrasaoPretoBrancoUrl,
          ImagemFundoHomeUrl,
          ImagemFundoTelasUrl,
          CorTema,
          Titulo,
          CorFonteTituloPortal,
          CorFonteTituloTelas,
          CorFonte,
          CorFonteRodape,
          CorFundoRodape,
          CorFundoGrid,
          CorFundoTituloGrid,
          CorTituloGrid,
        } = responseConfiguracao.data;

        const {
          RazaoSocial,
          ChaveCliente,
          Endereco,
          Bairro,
          Cidade,
          CEP,
          Telefone,
          Atendimento,
          EmailSocials,
          FacebookSocials,
          InstagramSocials,
          LinkedInSocials,
          TwitterSocials,
          WhatsAppSocials,
          YoutubeSocials,
        } = responseCliente.data;

        dispatch(
          setConfiguration({
            BannerUrl: getFullUrl(BannerUrl),
            BrasaoUrl: getFullUrl(BrasaoUrl),
            BrasaoPretoBrancoUrl: getFullUrl(BrasaoPretoBrancoUrl),
            ImagemFundoHomeUrl: getFullUrl(ImagemFundoHomeUrl),
            ImagemFundoTelasUrl: getFullUrl(ImagemFundoTelasUrl),
            CorTema,
            Titulo,
            CorFonteTituloPortal,
            CorFonteTituloTelas,
            CorFonte,
            CorFonteRodape,
            CorFundoRodape,
            RazaoSocial,
            ChaveCliente,
            Endereco,
            Bairro,
            Cidade,
            CEP,
            Telefone,
            Atendimento,
            CorFundoGrid,
            CorFundoTituloGrid,
            CorTituloGrid,
            EmailSocials,
            FacebookSocials,
            InstagramSocials,
            LinkedInSocials,
            TwitterSocials,
            WhatsAppSocials,
            YoutubeSocials,
          })
        );

        setBackTopBgColor(CorTema);

        // Caso o LESS não esteja carregado ainda, apenas segue com o tema padrão.
        if (window.less && typeof window.less.modifyVars === 'function') {
          const corTexto = ensureContrast(
            CorFonte,
            AA_SURFACE,
            4.5,
            AA_TEXT_PRIMARY_ON_LIGHT
          );
          // Sider/menu usam texto branco — background precisa ser escuro o bastante (AA).
          const corTemaAa = ensureBackgroundForForeground(
            CorTema,
            '#ffffff',
            4.5,
            '#1a1a1a'
          );
          const corTituloGridAa = ensureContrast(
            CorTituloGrid,
            CorFundoTituloGrid || '#fff2f2'
          );

          await window.less.modifyVars({
            '@primary-color': corTemaAa || 'rgba(0, 0, 0, 0.1);',
            '@icon-color-hover': '#f5f5f5',
            '@link-hover-decoration': '#ffffff',
            '@layout-sider-background': corTemaAa || '#036f5a',
            '@menu-dark-bg': `${corTemaAa} !important` || '#036f5a',
            '@layout-footer-background': CorFundoRodape || '#fff',
            '@heading-color': corTexto,
            '@text-color': corTexto,
            '@text-color-secondary': AA_TEXT_ON_LIGHT,
            '@disabled-color': AA_TEXT_ON_LIGHT,
            '@link-color': AA_LINK_ON_LIGHT,
            '@link-hover-color': AA_LINK_HOVER_ON_LIGHT,
            // Titulo Grid
            '@table-header-color': corTituloGridAa || '#000',
            '@table-footer-color': corTituloGridAa || '#000',
            // Fundo Grid
            '@table-bg': CorFundoGrid || '#f2f2f2',
            '@table-body-sort-bg': CorFundoGrid || '#f2f2f2',
            // Fundo Titulo Grid
            '@table-header-bg': CorFundoTituloGrid || '#fff2f2',
            '@table-header-sort-bg': CorFundoTituloGrid || '#f2f2f2',
            '@table-header-sort-active-bg': CorFundoTituloGrid || '#f2f2f2',
            '@table-footer-bg': CorFundoTituloGrid || '#f2f2f2',
          });
        }
      } catch (error) {
        console.error('[DefaultLayout] Falha ao carregar configuracao/cliente', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    getConfiguracao();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  // Shell utilizável (config/cliente + MenuPortal) → libera VLibras opcional
  useEffect(() => {
    if (!isLoading && !isLoadingMenus) {
      dispatch(setPortalBootReady());
    }
  }, [isLoading, isLoadingMenus, dispatch]);

  // Texto de loading só após 4s — evita flash em carga rápida
  useEffect(() => {
    const stillLoading = isLoading || isLoadingMenus;
    if (!stillLoading) {
      setShowLoadingText(false);
      return undefined;
    }
    const timerId = window.setTimeout(() => setShowLoadingText(true), 4000);
    return () => window.clearTimeout(timerId);
  }, [isLoading, isLoadingMenus]);

  function handleToggle() {
    setOpenKeys([]);
    setCollapsed(prev => !prev);
  }

  if (isLoading || isLoadingMenus) {
    return (
      <Layout>
        <Layout id="layout">
          <Content>
            <h1
              style={
                showLoadingText
                  ? undefined
                  : {
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    }
              }
            >
              Carregando Portal de Acesso à Informação
            </h1>
            {showLoadingText && (
              <p role="status" aria-live="polite">
                Aguarde enquanto o portal é carregado.
              </p>
            )}
          </Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout hasSider={!isMobile}>
      {isMobile ? (
        <Drawer
          placement="left"
          visible={collapsed}
          closable={false}
          onClose={handleToggle}
        >
          <Sider>
            <Logo />
            <Menu
              openKeys={openKeys}
              setOpenKeys={setOpenKeys}
              setCollapsed={setCollapsed}
            />
          </Sider>
        </Drawer>
      ) : (
        <Sider collapsed={collapsed}>
          <Logo />
          <Menu
            openKeys={openKeys}
            setOpenKeys={setOpenKeys}
            collapsed={collapsed}
          />
          <SiderStyled.SiderCollapseTrigger>
            <Collapse collapsed={collapsed} handleToggle={handleToggle} />
          </SiderStyled.SiderCollapseTrigger>
        </Sider>
      )}

      {/* Coluna principal: só header + main + footer (landmarks sem aninhamento) */}
      <Layout id="layout">
        <Header
          leftBar={
            isMobile ? (
              <Collapse
                collapsed={collapsed}
                handleToggle={handleToggle}
                isMobileTrigger
              />
            ) : null
            /* Desktop: botão fica no Sider; mobile: botão no banner para abrir o drawer */
          }
        >
          <HeaderTitleSlot>
            <Title />
          </HeaderTitleSlot>

          <HeaderAcessibilitySlot>
            <Acessibility />
          </HeaderAcessibilitySlot>
        </Header>
        <Content>{children}</Content>
        <Footer />
      </Layout>

      {/* complementary fora da coluna main/footer — AccessMonitor landmark_04 */}
      <Socials />
      <BackTopPage
        icon={<Icons.UpOutlined />}
        backgroundColor={backTopBgColor || 'rgba(0, 0, 0, 0.1);'}
      />
    </Layout>
  );
}

export default DefaultLayout;
